import { useState } from "react";
import { PenTool } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Designer } from "@/components/designer/Designer";
import { fromLayout } from "@/lib/design";

const TYPE_HINT = { casement: "casement", tilt_turn: "casement", awning: "awning", fixed: "fixed", sliding_window: "slid", sliding_door: "patio", swing_door: "french",
  single_hung: "single hung", double_hung: "double hung", bifold_door: "bi-fold door", bifold_window: "bi-fold window" };

// Quote-line fields for a saved design (server re-prices and snapshots on save).
export function designPatch(design, price, owner, types = [], row = {}) {
  const hint = TYPE_HINT[price.main_category] || "";
  const found = types.find((x) => hint && x.name.toLowerCase().includes(hint));
  // set the type for new/placeholder lines, or when the assembly's main piece changes category (e.g. a window becomes a door assembly)
  const t = found && (!row.type_id || row.type_name === "Unmapped" || row.match_status === "designer" || !(row.type_name || "").toLowerCase().includes(hint)) ? found : null;
  return {
    design, width_in: design.w, height_in: design.h, design_summary: price.summary || "", pricing_mode: "component", layout: "",
    screen_name: price.screen_name || "",
    ...(price.snapshot !== undefined ? { design_snapshot: price.snapshot || "" } : {}),
    ...(t ? { type_id: t.id, type_name: t.name, category: t.category, sketch: t.sketch } : {}),
    ...(price.main_line_id ? { product_line_id: price.main_line_id } : {}),
    component: owner ? { design_unit: price.cost, breakdown: price.parts, warn: price.warn } : {},
    component_unit_sell: price.suggested, no_line: false,
    ...(row.match_status === "designer" ? { match_status: "", match_label: "", price_override: null, price_source: "" } : {}),
  };
}

// Full-screen builder dialog (Edit design on a quote line, or "Open in Designer" from a combined-unit reading).
export function DesignDialog({ row, idx, onPatch, owner, types, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-6xl overflow-y-auto" data-testid="design-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Design line {idx + 1}</DialogTitle>
          <DialogDescription>Tap a piece to select it. Add sidelites, transoms or stacked units, type exact sizes, set grids per section. The price updates live.</DialogDescription>
        </DialogHeader>
        <Designer initial={designFor(row)} saveLabel="Save to line" onSave={(d, p) => { onPatch(designPatch(d, p, owner, types, row)); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}

export const designFor = (row) => row.design?.root ? row.design
  : fromLayout(row.config && row.component?.line?.kind === "slider" ? row.config : row.layout, Number(row.width_in) || 36, Number(row.height_in) || 48,
    row.component?.line?.category || "casement", row.component?.line?.kind === "slider", row.slide_dirs || "", row.panel_widths || []);

export function DesignButton({ row, idx, onPatch, owner, types }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={`quote-item-design-button-${idx}`} onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1 rounded-md border border-slate-800 px-1.5 py-0.5 text-[11px] font-semibold text-slate-300 transition-colors hover:border-amber-500/60 hover:text-amber-500">
        <PenTool className="h-3 w-3" /> {row.design?.root ? "Edit design" : "Design"}
      </button>
      {open && <DesignDialog row={row} idx={idx} onPatch={onPatch} owner={owner} types={types} onClose={() => setOpen(false)} />}
    </>
  );
}
